import type { PredictionCategory } from '../types/prediction'

function flattenCategoryValue(value: unknown): string[] {
  if (typeof value === 'string' && value.trim()) {
    return [value.trim()]
  }

  if (Array.isArray(value)) {
    return value.flatMap(flattenCategoryValue)
  }

  if (typeof value === 'object' && value !== null) {
    return Object.values(value).flatMap(flattenCategoryValue)
  }

  return []
}

function categoryFromOfficialText(text: string): PredictionCategory | undefined {
  if (/crypto|cryptocurrency|bitcoin|ethereum|solana|dogecoin|digital asset|token market|\busdt\b|\busdc\b|\bbtc\b|\beth\b|\bxrp\b|\bdoge\b|\bbnb\b|\bavax\b|\bsui\b/.test(text)) {
    return 'crypto'
  }

  if (/sports?|nba|nfl|nhl|mlb|wnba|ncaa|soccer|football|basketball|baseball|hockey|tennis|golf|formula\s*1|\bf1\b|drivers['’]? champion|constructors['’]? champion/.test(text)) {
    return 'sports'
  }

  if (/entertainment|movie|film|music|album|oscar|grammy|television|celebrity/.test(text)) {
    return 'entertainment'
  }

  if (/finance|financial|business|econom|market|stock|fed|rate|inflation|gdp|trade|bank|treasury/.test(text)) {
    return 'finance'
  }

  if (/technology|tech|software|artificial intelligence|\bai\b|model|chip|semiconductor/.test(text)) {
    return 'technology'
  }

  if (/world|global|politic|international|geopolitic|current affairs/.test(text)) {
    return 'world'
  }

  return undefined
}

function categoryFromSemanticText(text: string): PredictionCategory | undefined {
  const aiTokenSignal = /(?:\b(?:input|output|context|prompt|completion|inference|reasoning)\s+tokens?\b|\b(?:tokens?|token)\s+(?:cost|pricing|price|usage|billing|per\s+million)\b|\b(?:llm|gpt|chatgpt|claude|gemini|openai|anthropic|model)\b[^?]{0,80}\b(?:tokens?|token\s+cost)\b)/.test(text)
  const explicitCryptoEntity = /(?:\b(?:btc|bitcoin|eth|ethereum|sol|solana|xrp|ripple|usdt|tether|usdc|usd\s*coin|dogecoin|bnb|binance\s*coin|avax|avalanche|ada|cardano|sui|aptos|ton|tron|trx|matic|polygon|altcoin)\b|\bdoge\b(?=\s+(?:price|market|up|down|coin|token|reserve|target|high|low)|[^?]{0,80}\$))/.test(text)
  const cryptoContext = /\b(?:crypto|cryptocurrency|stablecoin|blockchain|defi|web3|memecoin|on[- ]chain|wallet|exchange|airdrop|smart contract|liquidity pool|crypto asset)\b/.test(text)
  const cryptoTokenStructure = /\b(?:token|coin)\s+(?:launch|sale|price|market\s+cap|supply|holders?|listing|unlock|airdrop)\b|\b(?:launch|sale|price|market\s+cap|supply|holders?|listing|unlock|airdrop)\s+(?:token|coin)\b/.test(text)
  const cryptoAssetSignal = explicitCryptoEntity || cryptoContext || (cryptoTokenStructure && !aiTokenSignal)
  const cryptoMarketSignal = ((explicitCryptoEntity || cryptoContext || (cryptoTokenStructure && !aiTokenSignal)) && /\b(?:price|market\s+cap|capitalization|etf|coin|token|stablecoin|crypto\s+asset)\b/.test(text)) || /\$\s*[a-z]{2,10}\b/.test(text)
  // Geopolitics and elections must win over generic technology/company words,
  // while an explicit crypto asset/price question remains crypto.
  if (/\bnord\s+stream\b/.test(text)) {
    return 'world'
  }
  const entertainmentSignal = /rotten tomatoes|box office|netflix|movie|film|television|tv show|album|grammy|oscar|billboard|song|celebrity|actor|actress|opening weekend|daily views?|monthly listeners?|debut|podcast|episode|season|concert|festival|headline|featured|exhibition|\bauthor\b|\bbook\b|rolling loud/.test(text)
  if (entertainmentSignal && /\b(?:president|governor|senator|mayor|prime minister)\b/.test(text) && !cryptoAssetSignal) {
    return 'entertainment'
  }
  if (aiTokenSignal && /\b(?:artificial intelligence|\bai\b|llm|gpt|chatgpt|claude|gemini|openai|anthropic|model|inference|prompt|completion)\b/.test(text)) {
    return 'technology'
  }
  if (
    /nord\s+stream|taiwan|ukraine|russia|russian|israel|iran|gaza|palestin|crimea|donbas|nato|\bwar\b|military|invasion|invade|ceasefire|sanction|sovereignty|territor|diplomatic|foreign policy|trade deal|president|presidential|election(?!\s+day)|nomination|candidate|gubernatorial|congress|senate|senator|governor|mayor|democrat|republican|politic|trump|biden|harris|mamdani|coup|regime|prime minister|monarch|head of state|peace prize|abraham accords|nuclear test|charged|indicted|federal charges?|criminal charges?|arrested/.test(text)
    && !cryptoAssetSignal
  ) {
    return 'world'
  }

  if (cryptoAssetSignal || cryptoMarketSignal) {
    return 'crypto'
  }

  if (/sports?|nba|nfl|nhl|mlb|wnba|ncaa|soccer|football|basketball|baseball|hockey|tennis|golf|formula\s*1|\bf1\b|drivers['’]? champion|constructors['’]? champion|match|tournament|playoff|championship|world cup|super bowl|olympic|ballon d['’]or|home runs?|total bases|total runs?|hits?|goals?|scored|\bvs\b/.test(text)) {
    return 'sports'
  }

  if (entertainmentSignal || /entertainment|music|\bstream(?:ing)?\b|luminate|singer|band|artist|newjeans/.test(text)) {
    return 'entertainment'
  }

  if (/fed|interest rate|inflation|gdp|cpi|finance|financial|economy|stock|market cap|earnings|revenue|tariff|trade|bank|treasury|recession|bond|yield|ipo|gold price/.test(text)) {
    return 'finance'
  }

  if (/artificial intelligence|\bai\b|llm|gpt|chatgpt|claude|gemini|openai|anthropic|mistral|machine learning|neural|robot|technology|software|chip|semiconductor|nvidia|spacex|nasa|rocket|tesla|apple|google|microsoft|product launch/.test(text)) {
    return 'technology'
  }

  return undefined
}

export function classifyPredictionCategory(...values: unknown[]): PredictionCategory {
  const officialText = values.length > 1
    ? flattenCategoryValue(values[0]).join(' ').toLowerCase()
    : ''
  const semanticValues = values.length > 1 ? values.slice(1) : values
  const semanticText = semanticValues.flatMap(flattenCategoryValue).join(' ').toLowerCase()
  const semanticCategory = categoryFromSemanticText(semanticText)

  if (semanticCategory) {
    return semanticCategory
  }

  return categoryFromOfficialText(officialText) ?? 'world'
}
